package com.schoolconnect.app.feature.common

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

internal data class PagedItems<T>(
    val items: List<T>,
    val totalCount: Int,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

internal data class ServerPagedWindow(
    val totalCount: Int,
    val isTotalCountExact: Boolean,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

data class ServerPagingState(
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val totalCount: Int = 0,
    val isTotalCountExact: Boolean = false
)

interface ServerPagedListUiState {
    val paging: ServerPagingState
}

internal fun ServerPagingState.withPageIndex(pageIndex: Int): ServerPagingState =
    copy(pageIndex = pageIndex.coerceAtLeast(0))

internal fun ServerPagingState.withPageSize(pageSize: Int): ServerPagingState =
    copy(pageSize = pageSize.coerceAtLeast(1), pageIndex = 0)

internal fun ServerPagingState.withTotals(
    totalCount: Int,
    isTotalCountExact: Boolean
): ServerPagingState = copy(
    totalCount = totalCount.coerceAtLeast(0),
    isTotalCountExact = isTotalCountExact
)

internal fun ServerPagingState.offset(): Int = pageIndex.coerceAtLeast(0) * pageSize.coerceAtLeast(1)

internal fun ServerPagingState.request(): PageRequest = PageRequest(pageIndex, pageSize)

internal fun <T> filterSortPage(
    items: List<T>,
    pageIndex: Int,
    pageSize: Int,
    predicate: (T) -> Boolean,
    comparator: Comparator<T>
): PagedItems<T> {
    val filtered = items.asSequence()
        .filter(predicate)
        .sortedWith(comparator)
        .toList()

    val safePageSize = pageSize.coerceAtLeast(1)
    val pageCount = maxOf(1, (filtered.size + safePageSize - 1) / safePageSize)
    val safePageIndex = pageIndex.coerceIn(0, pageCount - 1)
    val start = safePageIndex * safePageSize
    val end = minOf(start + safePageSize, filtered.size)

    return PagedItems(
        items = filtered.subList(start, end),
        totalCount = filtered.size,
        startIndex = if (filtered.isEmpty()) 0 else start + 1,
        endIndex = end,
        pageCount = pageCount
    )
}

internal fun ServerPagingState.toWindow(currentItemCount: Int): ServerPagedWindow {
    val safePageSize = pageSize.coerceAtLeast(1)
    val safePageIndex = pageIndex.coerceAtLeast(0)
    val safeTotalCount = totalCount.coerceAtLeast(0)
    val pageCount = ((safeTotalCount + safePageSize - 1) / safePageSize).coerceAtLeast(1)
    val hasRows = currentItemCount > 0 && safeTotalCount > 0
    val startIndex = if (hasRows) (safePageIndex * safePageSize) + 1 else 0
    val endIndex = if (hasRows) minOf((safePageIndex + 1) * safePageSize, safeTotalCount) else 0

    return ServerPagedWindow(
        totalCount = safeTotalCount.coerceAtLeast(currentItemCount),
        isTotalCountExact = isTotalCountExact,
        startIndex = startIndex,
        endIndex = endIndex,
        pageCount = pageCount
    )
}

internal fun ServerPagingState.footerText(
    currentItemCount: Int,
    itemLabel: String
): String = serverPageFooterText(toWindow(currentItemCount), itemLabel)

internal fun serverPageFooterText(
    window: ServerPagedWindow,
    itemLabel: String
): String {
    val label = if (window.totalCount == 1 && itemLabel.endsWith("s")) {
        itemLabel.dropLast(1)
    } else {
        itemLabel
    }

    return when {
        window.totalCount == 0 -> "Showing 0 to 0 of 0 $label"
        window.startIndex == 0 || window.endIndex == 0 -> {
            val suffix = if (window.isTotalCountExact) "" else "+"
            "Showing 0 to 0 of ${window.totalCount}$suffix $label"
        }
        window.isTotalCountExact -> "Showing ${window.startIndex} to ${window.endIndex} of ${window.totalCount} $label"
        else -> "Showing ${window.startIndex} to ${window.endIndex} of ${window.totalCount}+ $label"
    }
}

internal fun compareDateFilter(rowDate: String, filterDate: String, lowerBound: Boolean): Boolean {
    val comparableFilter = toDateComparable(filterDate) ?: return true
    val comparableRow = toDateComparable(rowDate) ?: return false
    return if (lowerBound) comparableRow >= comparableFilter else comparableRow <= comparableFilter
}

internal fun compareSmartText(left: String, right: String): Int {
    val leftDate = toDateComparable(left)
    val rightDate = toDateComparable(right)
    if (leftDate != null && rightDate != null) {
        return leftDate.compareTo(rightDate)
    }
    return compareNaturalText(left, right)
}

internal fun <T> compareBySmartText(selector: (T) -> String): Comparator<T> =
    Comparator { left, right -> compareSmartText(selector(left), selector(right)) }

internal fun toDateComparable(value: String): Int? {
    val trimmed = value.trim()
    if (trimmed.isBlank()) return null

    val slashParts = trimmed.split("/")
    if (slashParts.size == 3) {
        val day = slashParts[0].toIntOrNull() ?: return null
        val month = slashParts[1].toIntOrNull() ?: return null
        val year = slashParts[2].toIntOrNull() ?: return null
        if (day !in 1..31 || month !in 1..12) return null
        return year * 10_000 + month * 100 + day
    }

    val dashParts = trimmed.split("-")
    if (dashParts.size == 3) {
        val first = dashParts[0].toIntOrNull() ?: return null
        val second = dashParts[1].toIntOrNull() ?: return null
        val third = dashParts[2].toIntOrNull() ?: return null
        return if (dashParts[0].length == 4) {
            val year = first
            val month = second
            val day = third
            if (day !in 1..31 || month !in 1..12) return null
            year * 10_000 + month * 100 + day
        } else {
            val day = first
            val month = second
            val year = third
            if (day !in 1..31 || month !in 1..12) return null
            year * 10_000 + month * 100 + day
        }
    }

    val spacedParts = trimmed.split(" ").filter { it.isNotBlank() }
    if (spacedParts.size == 3) {
        val day = spacedParts[0].toIntOrNull() ?: return null
        val month = monthNumberForShortName(spacedParts[1]) ?: return null
        val year = spacedParts[2].toIntOrNull() ?: return null
        if (day !in 1..31) return null
        return year * 10_000 + month * 100 + day
    }

    return null
}

private fun compareNaturalText(left: String, right: String): Int {
    val leftTrimmed = left.trim()
    val rightTrimmed = right.trim()

    if (leftTrimmed.isEmpty() || rightTrimmed.isEmpty()) {
        return when {
            leftTrimmed.isEmpty() && rightTrimmed.isEmpty() -> 0
            leftTrimmed.isEmpty() -> -1
            else -> 1
        }
    }

    val leftChunks = NATURAL_CHUNK_REGEX.findAll(leftTrimmed).map { it.value }.iterator()
    val rightChunks = NATURAL_CHUNK_REGEX.findAll(rightTrimmed).map { it.value }.iterator()

    while (leftChunks.hasNext() && rightChunks.hasNext()) {
        val leftChunk = leftChunks.next()
        val rightChunk = rightChunks.next()
        val chunkCompare = compareNaturalChunk(leftChunk, rightChunk)
        if (chunkCompare != 0) return chunkCompare
    }

    return when {
        leftChunks.hasNext() -> 1
        rightChunks.hasNext() -> -1
        else -> 0
    }
}

private fun compareNaturalChunk(left: String, right: String): Int {
    val leftIsDigit = left.firstOrNull()?.isDigit() == true
    val rightIsDigit = right.firstOrNull()?.isDigit() == true

    if (leftIsDigit && rightIsDigit) {
        val leftNumber = left.toLongOrNull()
        val rightNumber = right.toLongOrNull()
        if (leftNumber != null && rightNumber != null) {
            val numberCompare = leftNumber.compareTo(rightNumber)
            if (numberCompare != 0) return numberCompare
        }
        val lengthCompare = left.length.compareTo(right.length)
        if (lengthCompare != 0) return lengthCompare
        return left.compareTo(right)
    }

    if (leftIsDigit != rightIsDigit) {
        return if (leftIsDigit) -1 else 1
    }

    return left.lowercase().compareTo(right.lowercase())
}

internal class DebouncedActionRunner(
    private val scope: CoroutineScope,
    private val delayMs: Long = DebouncePolicy.DEFAULT_DELAY_MS
) {
    private var job: Job? = null

    fun submit(immediate: Boolean = false, action: suspend () -> Unit) {
        job?.cancel()
        job = scope.launch {
            if (!immediate) {
                delay(delayMs)
            }
            action()
        }
    }

    fun cancel() {
        job?.cancel()
        job = null
    }
}

private fun monthNumberForShortName(value: String): Int? = when (value.lowercase()) {
    "jan" -> 1
    "feb" -> 2
    "mar" -> 3
    "apr" -> 4
    "may" -> 5
    "jun" -> 6
    "jul" -> 7
    "aug" -> 8
    "sep" -> 9
    "oct" -> 10
    "nov" -> 11
    "dec" -> 12
    else -> null
}

private val NATURAL_CHUNK_REGEX = Regex("\\d+|\\D+")
